<div class="page">
  <div class="fx-progress" aria-hidden="true"></div>
  <canvas class="fx-dust" aria-hidden="true"></canvas>

  <header class="hero">
    <nav class="nav">
      <div class="logo">Abdurrab</div>
      <div class="nav-links">
        <a href="#about">About</a>
        <a href="#work">Work</a>
        <a href="#services">Services</a>
        <a href="#skills">Skills</a>
        <a href="#toolbox">Toolbox</a>
        <a href="#contact">Contact</a>
      </div>
      <div class="nav-actions">
        <button
          type="button"
          class="theme-toggle"
          [class.is-light]="theme === 'light'"
          (click)="toggleTheme()"
          [attr.aria-label]="theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'"
          [attr.data-tip]="theme === 'dark' ? 'Light mode? Bold choice. Sunglasses recommended.' : 'Back to the dark side? We have chai.'"
        >
          <svg class="icon-sun" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
          </svg>
          <svg class="icon-moon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M20.5 14.5A8.5 8.5 0 0 1 9.5 3.5a8.5 8.5 0 1 0 11 11Z" />
          </svg>
        </button>
        <a class="cta" href="#contact">Let’s build</a>
      </div>
    </nav>

    <div class="hero-grid">
      <div class="hero-text">
        <p class="eyebrow">Hey, I’m Abdurrab · Full-Stack Engineer</p>
        <h1 data-split data-accent-last="2">I turn messy requirements into clean engineering.</h1>
        <p class="lead">
          Full-stack engineer, 4 years in. Backend at heart (Java, Spring Boot), frontend by choice (Angular, React), and
          usually the one who actually reads the logs. I’ve shipped for fintech, enterprise and internal-tools teams, and I
          take on freelance builds end to end.
        </p>
        <div class="hero-actions">
          <a class="btn primary" href="#work">View projects</a>
          <a class="btn ghost" href="#contact">Start a conversation</a>
        </div>
        <div class="highlight-row">
          <div class="highlight" *ngFor="let item of highlights">
            <span class="highlight-label">{{ item.label }}</span>
            <span class="highlight-detail">{{ item.detail }}</span>
          </div>
        </div>
      </div>

      <div class="hero-card">
        <div class="hero-card-inner">
          <div class="profile-wrap">
            <span class="profile-orbit"></span>
            <img class="profile-photo" [src]="profileImage" alt="Portrait of Abdurrab" />
          </div>
          <div class="pulse"></div>
          <h3>Currently obsessed with</h3>
          <ul>
            <li>Microservices and REST APIs with Java and Spring Boot</li>
            <li>Responsive web apps in Angular and React</li>
            <li>Cloud deployments, CI/CD and observability on AWS</li>
          </ul>
          <div class="tag-row">
            <span>Based in Noida · Working globally</span>
            <strong>Open to full-time roles &amp; freelance projects</strong>
          </div>
        </div>
      </div>
    </div>
  </header>

  <section id="about" class="section about">
    <div>
      <h2>Who’s this?</h2>
      <p>
        Engineer by profession, debugger by instinct. Four years across fintech, enterprise platforms and internal tools
        taught me two things: every “quick fix” has a sequel, and the best systems are the boring ones that never wake you
        up at 3 a.m. I own things end to end, from backend services and frontends to integrations and CI/CD, and I’m happy
        to take a product from napkin sketch to production on my own.
      </p>
    </div>
    <div class="about-card">
      <h3>Things I care about</h3>
      <p>Clear communication, pragmatic engineering, and systems that scale without turning into a maze. Also, good variable names.</p>
      <div class="about-tags">
        <span>Product-minded</span>
        <span>Quality-obsessed</span>
        <span>Owns it end to end</span>
      </div>
    </div>
  </section>

  <section id="work" class="section work">
    <h2>Stuff I’ve shipped</h2>
    <div class="work-grid">
      <article class="work-card" *ngFor="let project of projects">
        <span class="project-domain">{{ project.domain }}</span>
        <h3>{{ project.name }}</h3>
        <p>{{ project.description }}</p>
        <div class="stack">{{ project.stack }}</div>
        <a *ngIf="project.link" class="project-link" [href]="project.link" target="_blank" rel="noopener noreferrer">
          Visit project
        </a>
      </article>
    </div>
  </section>

  <section id="services" class="section services">
    <h2>How I can help</h2>
    <div class="services-grid">
      <article class="service" *ngFor="let service of services">
        <h3>{{ service.title }}</h3>
        <p>{{ service.description }}</p>
      </article>
    </div>
  </section>

  <section id="skills" class="section stack-section">
    <h2>My toolbox</h2>
    <p class="stack-intro">
      These are my daily drivers, but I’m a problem solver first and a <em>stack loyalist</em> never. Hand me a new
      language, framework or a codebase nobody wants to touch, and I’ll be shipping in it before my chai gets cold.
    </p>
    <div class="skill-groups">
      <div class="skill-group" *ngFor="let group of skillGroups; let i = index" [class.is-open]="isOpen(group.label)">
        <h3 class="skill-heading">
          <button
            type="button"
            class="skill-toggle"
            (click)="toggleGroup(group.label)"
            [attr.aria-expanded]="isOpen(group.label)"
            [attr.aria-controls]="'skill-panel-' + i"
          >
            <span class="skill-name">{{ group.label }}</span>
            <span class="skill-meta">{{ group.items.length }} tools</span>
            <span class="skill-plus" aria-hidden="true"></span>
            <span class="skill-preview">
              {{ group.items[0] }} · {{ group.items[1] }}
              <span class="skill-more" *ngIf="group.items.length > 2">+{{ group.items.length - 2 }} more</span>
            </span>
          </button>
        </h3>
        <div class="skill-panel" [id]="'skill-panel-' + i" role="region" [attr.aria-label]="group.label + ' skills'">
          <div class="skill-panel-inner">
            <div class="skill-list">
              <span class="skill" *ngFor="let skill of group.items; let j = index" [style.--i]="j">{{ skill }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <section class="section timeline">
    <h2>Where I’ve been</h2>
    <div class="timeline-grid">
      <div class="timeline-card" *ngFor="let item of experiences">
        <h3>{{ item.role }}</h3>
        <p class="timeline-meta">{{ item.company }} · {{ item.location }}</p>
        <p class="timeline-meta">{{ item.period }}</p>
        <p>{{ item.description }}</p>
      </div>
    </div>
  </section>

  <section id="toolbox" class="section toolbox">
    <h2>Developer toolbox</h2>
    <p class="stack-intro">
      Five little utilities I reach for every week. They run entirely in your browser: nothing passes through my server,
      and nothing you paste is logged or saved.
    </p>
    <app-toolbox />
  </section>

  <section id="contact" class="section contact">
    <div>
      <h2>Let’s talk</h2>
      <p>Hiring for a full-stack or backend role, or got a build that needs a dependable developer? I’m open to full-time roles and freelance projects, so pick a channel and say hi.</p>
      <p class="contact-aside">
        Not hiring? Say hi anyway. I’m happy to start with a random chat on any topic, and if it clicks, we can continue
        over a cutting chai. Got a half-baked idea? Let’s <em>bake it together</em>.
      </p>
    </div>
    <div class="contact-card">
      <!-- <p class="contact-kicker">Available for backend-heavy roles</p> -->
      <p class="contact-title">Say hi</p>
      <p class="contact-subtitle">I reply faster than a cached response.</p>
      <div class="contact-icons">
        <a class="contact-icon-btn mail" href="mailto:abdurrabkhan999@gmail.com?subject=Portfolio%20Inquiry" aria-label="Send email" data-tip="abdurrabkhan999@gmail.com&#10;Inbox is open. I reply faster than my CI pipeline.">
          <svg viewBox="0 0 24 24" focusable="false">
            <path d="M3 6h18v12H3V6Zm2 2v.2l7 4.67 7-4.67V8l-7 4.67L5 8Z" />
          </svg>
        </a>
        <a class="contact-icon-btn phone" href="tel:+917703048142" aria-label="Call phone" data-tip="Ring ring only if it’s really urgent. For everything else, WhatsApp is right next door.">
          <svg viewBox="0 0 24 24" focusable="false">
            <path d="M6.62 10.79a15.5 15.5 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.01-.24c1.11.37 2.29.56 3.5.56a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.4 21 3 13.6 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.21.19 2.39.56 3.5a1 1 0 0 1-.24 1.01l-2.2 2.28Z" />
          </svg>
        </a>
        <a class="contact-icon-btn whatsapp" href="https://wa.me/917703048142?text=Hi%20Abdurrab%2C%20I%20came%20across%20your%20portfolio" target="_blank" rel="noopener noreferrer" aria-label="Chat on WhatsApp" data-tip="Fastest way to reach me. Drop a hi and I’ll get back quickly.">
          <svg viewBox="0 0 24 24" focusable="false">
            <path fill-rule="evenodd" d="M12 2.5a9.5 9.5 0 0 0-8.2 14.3L2.5 21.5l4.8-1.3A9.5 9.5 0 1 0 12 2.5Zm0 1.8a7.7 7.7 0 1 1-4 14.3l-.3-.2-2.8.8.8-2.7-.2-.3A7.7 7.7 0 0 1 12 4.3ZM9.2 7.6c-.2-.4-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2c0 1.3.9 2.5 1 2.7.2.2 1.8 2.8 4.4 3.8 2.2.9 2.6.7 3.1.6.5 0 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.1-.2-.2-.5-.3l-1.7-.8c-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.3 6.3 0 0 1-3.1-2.7c-.2-.4.2-.4.7-1.3.1-.2 0-.3 0-.4l-.8-1.9Z" />
          </svg>
        </a>
        <a class="contact-icon-btn github" href="https://github.com/AbdurrabCodes" target="_blank" rel="noopener noreferrer" aria-label="Open GitHub" data-tip="Where my side projects live (and a few quietly die).">
          <svg viewBox="0 0 24 24" focusable="false">
            <path d="M12 .5a12 12 0 0 0-3.79 23.4c.6.11.82-.25.82-.58v-2.1c-3.34.73-4.04-1.42-4.04-1.42a3.18 3.18 0 0 0-1.33-1.76c-1.09-.74.08-.73.08-.73a2.52 2.52 0 0 1 1.84 1.24 2.57 2.57 0 0 0 3.51 1 2.58 2.58 0 0 1 .77-1.61c-2.66-.3-5.46-1.33-5.46-5.93A4.64 4.64 0 0 1 5.64 8.8a4.3 4.3 0 0 1 .12-3.18s1-.33 3.3 1.25a11.4 11.4 0 0 1 6 0c2.28-1.58 3.3-1.25 3.3-1.25a4.3 4.3 0 0 1 .12 3.18 4.64 4.64 0 0 1 1.24 3.22c0 4.61-2.8 5.62-5.47 5.92a2.89 2.89 0 0 1 .82 2.24v3.32c0 .33.22.7.83.58A12 12 0 0 0 12 .5Z" />
          </svg>
        </a>
        <a class="contact-icon-btn linkedin" href="https://linkedin.com/in/abdurrab999" target="_blank" rel="noopener noreferrer" aria-label="Open LinkedIn" data-tip="The professional me. Suit and tie included.">
          <svg viewBox="0 0 24 24" focusable="false">
            <path d="M4.98 3.5A2.5 2.5 0 1 0 5 8.5a2.5 2.5 0 0 0-.02-5ZM3 9h4v12H3V9Zm7 0h3.83v1.72h.05A4.2 4.2 0 0 1 17.66 9c4.03 0 4.77 2.65 4.77 6.1V21h-4v-5.2c0-1.24-.02-2.83-1.72-2.83-1.72 0-1.99 1.35-1.99 2.74V21h-4V9Z" />
          </svg>
        </a>
        <a class="contact-icon-btn instagram" href="https://www.instagram.com/abdurrab.in/" target="_blank" rel="noopener noreferrer" aria-label="Open Instagram" data-tip="Trips, travel photos and random moments. Zero code, I promise.">
          <svg viewBox="0 0 24 24" focusable="false">
            <path fill-rule="evenodd" d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7Zm5 3.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Zm0 2a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Zm5.3-4a1.2 1.2 0 1 1 0 2.4 1.2 1.2 0 0 1 0-2.4Z" />
          </svg>
        </a>
      </div>
      <app-feedback />
    </div>
  </section>

  <div class="theme-toast" [class.is-visible]="toast()" role="status" aria-live="polite">{{ toast() }}</div>

  <footer class="footer">
    <p class="footer-sher">
      <span class="sher-intro">Arz kiya hai…</span>
      <span class="sher-line">Log kehte hain mehnat se banti hai website,</span>
      <span class="sher-line">Humne chai pi, aur bola, “AI, make it right.”</span>
    </p>
    <p>© {{ year }} Abdurrab. Built with Angular, way too much chai and a lot of very polite prompts.</p>
  </footer>
</div>
